import { RefObject, useEffect } from 'react';

type UseClickOutsideOptions = {
  closeOnScroll?: boolean;
};

export const useClickOutside = <T extends HTMLElement = HTMLElement>(
  ref: RefObject<T | null>,
  handler: () => void,
  excludeRefs?: RefObject<HTMLElement | null>[],
  options?: UseClickOutsideOptions
) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      if (ref.current && !ref.current.contains(target)) {
        const isExcluded = excludeRefs?.some((excludeRef) => excludeRef.current?.contains(target));

        if (!isExcluded) {
          handler();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [ref, handler, excludeRefs]);

  useEffect(() => {
    if (!options?.closeOnScroll) return;

    const handleScroll = () => {
      handler();
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [handler, options?.closeOnScroll]);
};
